import { Box, HStack, Icon, Stack, Text, VStack } from "@chakra-ui/react";
import React from "react";
import RegularLayout from "../components/layout/Layout";
import { graphql } from "gatsby";
import BlogHeroPostSection from "../components/organisms/BlogHeroPostSection";

import "../global.css";
import RelatedPost from "../components/molecules/RelatedPosts";
import ICONS from "../constants/socialMedia";
import BlogCTABanner from "../components/molecules/BlogCtaBanner";
import { SEO } from "../components/atoms/Seo";

const BlogPost = ({ data }: any) => {
  const post = data?.wpPost;

  const categories = post.categories.nodes.map((category: any) => ({
    name: category.name,
    slug: category.slug,
  }));

  const postTitle = post.title;
  const postSlug = post.slug;

  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Blog", link: "/blog" },
    { label: postTitle, link: `/blog/${categories[0].slug}/${postSlug}` },
  ];

  const title = post.title;
  const date = new Date(post.date);
  const formattedDate = `${date.getDate()} ${date.toLocaleString("en-US", {
    month: "short",
  })} ${date.getFullYear()}`;

  function addClassToHeadings(htmlContent: any) {
    return htmlContent
      .replace(/<h2([^>]*)>/g, '<h2 class="medium-text"$1>')
      .replace(/<h3([^>]*)>/g, '<h3 class="medium-text"$1>')
      .replace(/<p([^>]*)>/g, '<p class="regular-text"$1>');
  }

  const postContent = addClassToHeadings(post.content);

  return (
    <RegularLayout crumbs={breadcrumbs} maxWidth="100%">
      <>
        <BlogHeroPostSection
          imageData={
            post.featuredImage?.node?.localFile?.childImageSharp
              ?.gatsbyImageData
          }
          date={formattedDate}
          title={title}
          category={categories[0].name}
          author={post.author.node.name}
          excerpt={post.excerpt}
        />
        <Stack
          pt={10}
          maxW="1280px"
          mx="auto"
          px={{ base: 4, "2xl": 0 }}
          pb={{ base: 10, md: 20 }}
          gap={0}
        >
          <HStack
            flexDirection={{ base: "column-reverse", xl: "row" }}
            alignItems="flex-start"
            gap={{ base: 5, lg: 0, "2xl": 10 }}
            w="100%"
          >
            <Stack
              gap={0}
              alignItems={{ base: "flex-start", md: "justify-content" }}
            >
              <VStack
                flexDirection={{
                  base: "column-reverse",
                  lg: "row",
                  xl: "column",
                }}
                w={{ base: "100%", xl: "230px" }}
                alignItems={{ base: "flex-start", lg: "space-between" }}
                justifyContent="space-between"
                gap={{ base: 0, lg: 5 }}
                pt={{ base: 0, sm: 5, lg: 5, xl: 10 }}
              >
                <Stack gap={0}>
                  <Text
                    w="100%"
                    className="light-text"
                    fontSize="24px"
                    lineHeight="100%"
                    pb={5}
                    pt={{ base: 5, lg: 0 }}
                  >
                    Share
                  </Text>
                  <Stack
                    gap={5}
                    justifyContent="flex-start"
                    display="flex"
                    flexDir="row"
                    w="200px"
                  >
                    {ICONS.map((icon): any => {
                      return (
                        <>
                          {icon.show ? (
                            <a
                              key={icon.id}
                              target="_blank"
                              rel="noopener noreferrer"
                              href={icon.link}
                            >
                              <Box
                                cursor="pointer"
                                position="relative"
                                width="44px"
                                height="44px"
                                borderRadius="99px"
                                bg="#116266"
                                _hover={{ bg: "#4C9195" }}
                              >
                                <Icon
                                  fill="#fff"
                                  top={2.5}
                                  right={2.5}
                                  position="absolute"
                                  width="24px"
                                  height="24px"
                                  as={icon.icon}
                                />
                              </Box>
                            </a>
                          ) : (
                            <></>
                          )}
                        </>
                      );
                    })}
                  </Stack>
                </Stack>
                <Stack w={{ base: "100%", lg: "280px" }}>
                  <Text
                    className="grotesk-regular"
                    fontSize="36px"
                    lineHeight="100%"
                  >
                    {post.author.node.name}
                  </Text>
                  <Text
                    color="#949D9B"
                    className="light-text"
                    lineHeight="100%"
                    fontSize="18px"
                  >
                    Automate your expense tracking with our advanced tools.
                    Categorize your expenditures
                  </Text>
                </Stack>
              </VStack>
              <Stack display={{ base: "flex", xl: "none" }}>
                <RelatedPost />
              </Stack>
            </Stack>
            <Box
              maxW={{ base: "640px", md: "100%", lg: "640px" }}
              w="100%"
              color="#6A7471"
              fontSize="16px"
              mx="auto"
              css={{
                a: {
                  textDecoration: "underline",
                  ":hover": { color: "#116266" },
                },
                h1: {
                  marginBottom: "10px",
                },
                h2: {
                  color: "#1B1E1E",
                  marginBottom: "10px",
                  paddingTop: "30px",
                  fontSize: "40px !important",
                  lineHeight: "100%",
                  fontWeight: "500 !important",
                  fontFamily: "Founders Grotesk Medium !important",
                },
                h3: {
                  paddingTop: "30px",
                  color: "#1B1E1E",
                  marginBottom: "10px",
                  fontSize: "40px !important",
                  lineHeight: "100%",
                  fontWeight: "500 !important",
                  fontFamily: "Founders Grotesk Medium !important",
                },
                p: {
                  paddingBottom: "10px",
                },

                "h3:first-of-type": {
                  marginTop: "0",
                },
                "p:first-of-type": {
                  marginTop: "30px",
                },
                h4: {
                  marginBottom: "10px",
                },
              }}
            >
              <Box
                mt={{ base: -10, lg: 0 }}
                dangerouslySetInnerHTML={{ __html: postContent }}
              />
              <BlogCTABanner />
            </Box>
            <Stack gap={0} display={{ base: "none", xl: "flex" }}>
              <RelatedPost />
            </Stack>
          </HStack>
        </Stack>
      </>
    </RegularLayout>
  );
};

export const Head = ({ data }: any) => {
  return (
    <SEO
      title={data.wpPost.seo.title}
      description={data.wpPost.seo.metaDesc}
      featuredImage={
        data.wpPost.featuredImage?.node?.localFile?.childImageSharp
          ?.gatsbyImageData
      }
      content="article"
    />
  );
};
export default BlogPost;

export const query = graphql`
  query PostQuery($slug: String!) {
    wpPost(slug: { eq: $slug }) {
      author {
        node {
          name
        }
      }
      date
      categories {
        nodes {
          name
          slug
        }
      }
      featuredImage {
        node {
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
          altText
        }
      }
      title
      excerpt
      slug
      content
      seo {
        title
        metaDesc
      }
    }
  }
`;
